import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Notification, NotificationWithRelations } from '../types/notifications';
import { useAuth } from './useAuth';

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<NotificationWithRelations[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      console.log('User autenticado, buscando notificações:', user.id);
      try {
        fetchNotifications();
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
      const unsubscribe = subscribeToNotifications();
      return () => {
        unsubscribe();
      };
    }
  }, [user]);

  const fetchNotifications = async () => {
    if (!user) {
      console.error('Tentativa de buscar notificações sem usuário');
      return [];
    }

    try {
      setLoading(true);
      console.log('Buscando notificações para o usuário:', user.id);
      
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro na query do Supabase:', error);
        throw error;
      }

      console.log('Notificações recebidas:', data);
      const typedData = data as NotificationWithRelations[];
      setNotifications(typedData);
      setUnreadCount(typedData.filter(n => !n.read).length);
      return typedData;
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
      setNotifications([]);
      setUnreadCount(0);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const subscribeToNotifications = () => {
    const subscription = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user?.id}`
        },
        (payload) => {
          console.log('Mudança nas notificações:', payload);
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (error) throw error;

      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user?.id)
        .eq('read', false);

      if (error) throw error;

      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Erro ao marcar todas notificações como lidas:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;

      setNotifications(prev =>
        prev.filter(n => n.id !== notificationId)
      );
      setUnreadCount(prev => 
        notifications.find(n => n.id === notificationId)?.read ? prev : Math.max(0, prev - 1)
      );
    } catch (error) {
      console.error('Erro ao apagar notificação:', error);
    }
  };

  const createNotification = async (notification: Partial<Notification>) => {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .insert([
          {
            ...notification,
            user_id: user?.id,
            read: false
          }
        ])
        .select();

      if (error) throw error;

      return data[0];
    } catch (error) {
      console.error('Erro ao criar notificação:', error);
      return null;
    }
  };

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    createNotification,
    fetchNotifications
  };
};